import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { UserClientUtils } from 'ducks/user-client';
import { UserServiceUrls } from '@portal/portal-core';

import { updatePersonalDataSuccess, updatePersonalDataFailure } from 'actions/user-account';
import { UPDATE_PERSONAL_DATA_REQUEST } from 'constants/user-account';
import { selectUserId } from 'selectors';

// Boundary type: the client config threaded through the root saga wrapper.
// `baseApiUrl` is read here; `getAuthToken` is forwarded to `UserClientUtils.sendAction`.
interface ClientConfig {
  baseApiUrl?: string;
  getAuthToken?: () => string | null | undefined;
}

// Boundary type: shape returned by `UserClientUtils.sendAction` (untyped `.js`).
interface SendActionResult {
  succeeded?: boolean;
  error?: unknown;
}

interface UpdatePersonalDataAction {
  type: typeof UPDATE_PERSONAL_DATA_REQUEST;
  personalData: {
    givenName?: string;
    familyName?: string;
  };
}

export function* updatePersonalData(
  config: ClientConfig,
  { personalData }: UpdatePersonalDataAction,
): Generator<unknown, void, unknown> {
  // Boundary: redux-saga effects yield `unknown`; assert the selector's result.
  const userId = (yield select(selectUserId)) as string;
  if (!userId) {
    yield put(updatePersonalDataFailure(new Error('Not logged in')));
    return;
  }

  const payload = {
    firstname: personalData.givenName,
    lastname: personalData.familyName,
  };

  const url = UserServiceUrls.userUrl(config.baseApiUrl, userId);
  // Boundary: redux-saga effects yield `unknown`; assert the call result shape.
  const { succeeded, error } = (yield call(
    UserClientUtils.sendAction,
    config,
    payload,
    url,
  )) as SendActionResult;

  if (succeeded) {
    yield put(updatePersonalDataSuccess());
  } else {
    yield put(updatePersonalDataFailure(error));
  }
}

/* istanbul ignore next */
function* watchUpdatePersonalData(config: ClientConfig): Generator<unknown, void, unknown> {
  yield takeLatest(UPDATE_PERSONAL_DATA_REQUEST, updatePersonalData, config);
}

/* istanbul ignore next */
export default (config: ClientConfig) =>
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([watchUpdatePersonalData(config)]);
  };
